import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, Loader2, Save, Plane } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tab = ({ id, active, onClick, children }) => (
  <button onClick={() => onClick(id)} data-testid={`admin-tab-${id}`}
    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${active === id ? "bg-sky-500 text-white" : "text-slate-300 hover:text-white"}`}>
    {children}
  </button>
);

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("bmj_admin_token") || "");
  const [keyInput, setKeyInput] = useState("");
  const [tab, setTab] = useState("settings");
  const [settings, setSettings] = useState(null);
  const [itineraries, setItineraries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);

  const headers = { "X-Admin-Key": token };

  const loadAll = useCallback(async (t) => {
    const h = { "X-Admin-Key": t };
    try {
      const [s, i, p, l] = await Promise.all([
        axios.get(`${API}/admin/settings`, { headers: h }),
        axios.get(`${API}/admin/itineraries`, { headers: h }),
        axios.get(`${API}/admin/payments`, { headers: h }),
        axios.get(`${API}/admin/leads`, { headers: h }),
      ]);
      setSettings(s.data); setItineraries(i.data.itineraries); setPayments(p.data.payments); setLeads(l.data.leads);
    } catch {
      toast.error("Session expired, please log in again");
      setToken(""); localStorage.removeItem("bmj_admin_token");
    }
  }, []);

  useEffect(() => { if (token) loadAll(token); }, [token, loadAll]);

  const handleLogin = async () => {
    try {
      const r = await axios.post(`${API}/admin/login`, { admin_key: keyInput });
      setToken(r.data.token);
      localStorage.setItem("bmj_admin_token", r.data.token);
    } catch {
      toast.error("Invalid admin key");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings`, {
        price_one_time: parseFloat(settings.price_one_time),
        price_monthly: parseFloat(settings.price_monthly),
        price_yearly: parseFloat(settings.price_yearly),
        free_preview_enabled: settings.free_preview_enabled,
        preview_days: parseInt(settings.preview_days),
        ai_prompt: settings.ai_prompt,
        currency: settings.currency,
        currency_symbol: settings.currency_symbol,
      }, { headers });
      setSettings(r.data);
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <Card className="bg-slate-800/70 border-slate-700 w-full max-w-sm">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-white"><Lock className="h-5 w-5 text-sky-400" /><span className="font-semibold">Admin Login</span></div>
            <Input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="Admin key"
              onKeyDown={(e) => e.key === "Enter" && handleLogin()} className="bg-slate-900 border-slate-700 text-white" data-testid="admin-key-input" />
            <Button onClick={handleLogin} className="w-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="admin-login-btn">Log in</Button>
          </CardContent>
        </Card>
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <header className="bg-slate-900/80 border-b border-slate-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Plane className="h-7 w-7 text-sky-400" /><span className="text-xl font-bold text-white">BMJ Admin</span></Link>
          <Button variant="ghost" onClick={() => { setToken(""); localStorage.removeItem("bmj_admin_token"); }} className="text-slate-300 hover:text-white" data-testid="admin-logout-btn">Logout</Button>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="flex gap-2 mb-6 bg-slate-800/60 p-1.5 rounded-lg w-fit">
          <Tab id="settings" active={tab} onClick={setTab}>Settings</Tab>
          <Tab id="itineraries" active={tab} onClick={setTab}>Itineraries ({itineraries.length})</Tab>
          <Tab id="payments" active={tab} onClick={setTab}>Payments ({payments.length})</Tab>
          <Tab id="leads" active={tab} onClick={setTab}>Leads ({leads.length})</Tab>
        </div>

        {tab === "settings" && settings && (
          <Card className="bg-slate-800/70 border-slate-700" data-testid="admin-settings">
            <CardContent className="p-6 space-y-5">
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label className="text-slate-200">One-time price</Label><Input type="number" step="0.01" value={settings.price_one_time} onChange={(e) => setSettings({ ...settings, price_one_time: e.target.value })} className="bg-slate-900 border-slate-700 text-white mt-1" data-testid="admin-price-onetime" /></div>
                <div><Label className="text-slate-200">Monthly price</Label><Input type="number" step="0.01" value={settings.price_monthly} onChange={(e) => setSettings({ ...settings, price_monthly: e.target.value })} className="bg-slate-900 border-slate-700 text-white mt-1" data-testid="admin-price-monthly" /></div>
                <div><Label className="text-slate-200">Yearly price</Label><Input type="number" step="0.01" value={settings.price_yearly} onChange={(e) => setSettings({ ...settings, price_yearly: e.target.value })} className="bg-slate-900 border-slate-700 text-white mt-1" data-testid="admin-price-yearly" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-slate-200">Currency code</Label><Input value={settings.currency} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} className="bg-slate-900 border-slate-700 text-white mt-1" data-testid="admin-currency" /></div>
                <div><Label className="text-slate-200">Currency symbol</Label><Input value={settings.currency_symbol} onChange={(e) => setSettings({ ...settings, currency_symbol: e.target.value })} className="bg-slate-900 border-slate-700 text-white mt-1" data-testid="admin-currency-symbol" /></div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-700 p-4">
                <div><p className="text-white font-medium">Free preview</p><p className="text-slate-400 text-sm">Show some days free before paywall</p></div>
                <Switch checked={settings.free_preview_enabled} onCheckedChange={(v) => setSettings({ ...settings, free_preview_enabled: v })} data-testid="admin-preview-toggle" />
              </div>
              <div><Label className="text-slate-200">Preview days (free)</Label><Input type="number" value={settings.preview_days} onChange={(e) => setSettings({ ...settings, preview_days: e.target.value })} className="bg-slate-900 border-slate-700 text-white mt-1 max-w-[120px]" data-testid="admin-preview-days" /></div>
              <div><Label className="text-slate-200">AI Prompt (system instruction)</Label><Textarea rows={5} value={settings.ai_prompt} onChange={(e) => setSettings({ ...settings, ai_prompt: e.target.value })} className="bg-slate-900 border-slate-700 text-white mt-1" data-testid="admin-ai-prompt" /></div>
              <Button onClick={saveSettings} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="admin-save-btn">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Settings
              </Button>
            </CardContent>
          </Card>
        )}

        {tab === "itineraries" && (
          <Card className="bg-slate-800/70 border-slate-700"><CardContent className="p-0">
            <table className="w-full text-sm" data-testid="admin-itineraries-table">
              <thead className="text-slate-400 border-b border-slate-700"><tr><th className="text-left p-3">Destination</th><th className="text-left p-3">Days</th><th className="text-left p-3">Paid</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Created</th></tr></thead>
              <tbody>{itineraries.map((it) => (
                <tr key={it.id} className="border-b border-slate-700/50 text-slate-200"><td className="p-3">{it.destination}</td><td className="p-3">{it.days}</td><td className="p-3">{it.paid ? <span className="text-emerald-400">Yes</span> : <span className="text-slate-500">No</span>}</td><td className="p-3">{it.plan || "-"}</td><td className="p-3">{it.created_at?.slice(0, 10)}</td></tr>
              ))}</tbody>
            </table>
            {itineraries.length === 0 && <p className="text-slate-500 p-6 text-center">No itineraries yet</p>}
          </CardContent></Card>
        )}

        {tab === "payments" && (
          <Card className="bg-slate-800/70 border-slate-700"><CardContent className="p-0">
            <table className="w-full text-sm" data-testid="admin-payments-table">
              <thead className="text-slate-400 border-b border-slate-700"><tr><th className="text-left p-3">Order</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Date</th></tr></thead>
              <tbody>{payments.map((p) => (
                <tr key={p.order_id} className="border-b border-slate-700/50 text-slate-200"><td className="p-3 font-mono text-xs">{p.order_id}</td><td className="p-3">{p.plan}</td><td className="p-3">{p.currency} {p.amount}</td><td className="p-3">{p.status === "paid" ? <span className="text-emerald-400">paid</span> : p.status}</td><td className="p-3">{p.created_at?.slice(0, 10)}</td></tr>
              ))}</tbody>
            </table>
            {payments.length === 0 && <p className="text-slate-500 p-6 text-center">No payments yet</p>}
          </CardContent></Card>
        )}

        {tab === "leads" && (
          <Card className="bg-slate-800/70 border-slate-700"><CardContent className="p-0">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="text-slate-400 border-b border-slate-700"><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Phone</th><th className="text-left p-3">Route</th><th className="text-left p-3">Date</th></tr></thead>
              <tbody>{leads.map((l, i) => (
                <tr key={i} className="border-b border-slate-700/50 text-slate-200"><td className="p-3">{l.full_name}</td><td className="p-3">{l.email}</td><td className="p-3">{l.phone}</td><td className="p-3">{l.departure_airport} → {l.arrival_airport}</td><td className="p-3">{l.created_at?.slice(0, 10)}</td></tr>
              ))}</tbody>
            </table>
            {leads.length === 0 && <p className="text-slate-500 p-6 text-center">No leads yet</p>}
          </CardContent></Card>
        )}
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
